import { stringify } from 'safe-stable-stringify';

import type { LogEvent } from '@Common/types/analytics/event';

import { getDeviceId } from '@sh4res/bo-common/utils/device_id';
import { isProdLikeEnv } from '@sh4res/bo-common/utils/env';

import { createAnalyticsEvent } from '@Utils/analytics/createAnalyticsEvent';

type LogParams = Record<string, unknown>;

const standardizeError = (error: unknown): { message: string; stack: string } => {
  if (error instanceof Error) {
    return { message: `${error.name}: ${error.message}`, stack: error.stack ?? '' };
  }

  return { message: stringify(error) ?? String(error), stack: '' };
};

/**
 * Log a business action, so we can replay what the user did before something went wrong.
 *
 * Pass identifiers and counts in `params` — never names, emails, IBANs or amounts.
 * See the PII note in the analytics README.
 *
 * @param message Short, stable description of the action
 * @param params Structured context, indexed as top-level fields in OpenSearch
 * @param level Severity, defaults to 'info'
 */
export const logAction = (
  message: string,
  params: LogParams | null = null,
  level: LogEvent['event']['level'] = 'info',
): void => {
  createAnalyticsEvent({ type: 'log', event: { level, message, params } });
};

/**
 * Log a handled error (a rejected mutation, a failed upload, a validation dead-end).
 *
 * @param message Short, stable description of what failed
 * @param error The caught error
 * @param params Structured context
 */
export const reportError = (message: string, error: unknown, params?: LogParams): void => {
  const { message: errorMessage, stack } = standardizeError(error);

  createAnalyticsEvent({
    type: 'error',
    event: {
      errorLevel: 'error',
      error: stringify({ message, error: errorMessage, ...params }) ?? errorMessage,
      errorStack: stack,
      componentStack: '',
    },
  });
};

/**
 * Log an unhandled render crash caught by an error boundary.
 *
 * @param error The thrown error
 * @param componentStack React component stack from the boundary
 */
export const reportCrash = (error: Error, componentStack: string): void => {
  createAnalyticsEvent({
    type: 'error',
    event: {
      errorLevel: 'JsCrash',
      error: error.toString(),
      errorStack: error.stack ?? '',
      componentStack,
    },
  });
};

/**
 * Log a page view.
 *
 * @param screenName Path the user landed on
 * @param previousScreenName Path they came from
 * @param screenParams Query string of the new location
 */
export const logNavigation = (
  screenName: string,
  previousScreenName: string,
  screenParams: string,
): void => {
  createAnalyticsEvent({
    type: 'navigate',
    event: {
      screenName,
      previousScreenName,
      // react-admin puts list filters in the query string, and those hold searched employee
      // names — record which params were present, never their values
      screenParams: { params: [...new URLSearchParams(screenParams).keys()] },
      navigatorName: 'main',
      parentName: 'main',
      parentParams: null,
      appRamUsage: '',
    },
  });
};

/**
 * Log the outcome of an API call.
 *
 * Only the request line and status are recorded — never request or response bodies, which on
 * this app carry employee and payment data.
 *
 * @param input Request line, outcome and timing
 */
export const logApiResponse = (input: {
  url: string;
  method: string;
  responseStatusCode: number;
  durationMs: number;
  succeeded: boolean;
}): void => {
  const { succeeded, url, ...rest } = input;
  // list requests carry filters like `?filter={"q":"<employee name>"}` — keep the path only
  const params = { ...rest, url: url.split('?')[0] };

  if (succeeded) {
    logAction('API call', params);

    return;
  }

  logAction('API call failed', params, input.responseStatusCode >= 500 ? 'error' : 'warn');
};

/**
 * Log browser and viewport info, so log lines can be correlated to an environment.
 */
export const logBrowserInfo = (): void => {
  createAnalyticsEvent({
    type: 'browser',
    event: {
      device_id: getDeviceId(),
      version: (window as { version_sha?: string }).version_sha ?? '',
      isDev: !isProdLikeEnv(),
      time_zone: new Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenSize: { height: window.innerHeight, width: window.innerWidth },
    },
  });
};
